import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { complianceAllowedForTier } from '../../lib/compliance-tier.js';
import { buildReportRows } from '../../lib/compliance-rows.js';
import { readinessView, READINESS_NOTE, READINESS_CARD, READINESS_ACCENT } from '../../lib/readiness-view.js';
import CisNotice, { mentionsCis } from '../../components/CisNotice.jsx';
import { COMPLIANCE_FEATURE } from '../../components/QlikUpsellCard.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';

// The Compliance card on the Overview (ENTRAID-307): the readiness level per framework from the latest succeeded
// report, read from the reports list only (no catalogs, no new route). Operators get the report status only; the API
// withholds the summary from them anyway. Display only: the API enforces role and tier on its own.

const FRAMEWORK_NAMES = Object.freeze({ 'cis-m365': 'CIS Microsoft 365', 'nist-800-53': 'NIST SP 800-53' });

// Operators and content roles with no succeeded report see the latest attempt's status, never "No report yet".
const lastReportLine = (row) => {
  const when = dateText(row.date);
  if (row.status === 'succeeded') return `Last report generated ${when}`;
  if (row.status === 'failed') return `Last report failed ${when}`;
  return `${row.statusLabel} ${when}`;
};

const dateText = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : '';
};

/** The readinessView input for one entry of a report summary; nothing assessed is derived from the coverage counts. */
function frameworkInput(id, entry, versions) {
  const cov = entry?.coverage;
  const nothing = cov && typeof cov === 'object'
    && !(cov.assessedAutomatically > 0) && !(cov.byAttestation?.total > 0);
  return {
    id: FRAMEWORK_NAMES[id] ?? id,
    version: versions.get(id) ?? '',
    readiness: typeof entry?.level === 'string' ? { level: entry.level, basis: nothing ? 'nothing_assessed' : undefined } : undefined,
    coverage: cov,
    counts: entry?.counts,
  };
}

// buildReportRows keeps the frameworks as text only; the card also needs their versions.
function withFrameworks(items, role) {
  const raw = new Map((Array.isArray(items) ? items : []).map((r) => [r?.reportId, r?.frameworks]));
  return buildReportRows(items, { role }).map((r) => ({ ...r, frameworks: raw.get(r.id) }));
}

export function cardFrameworks(row) {
  const summary = row?.summary && typeof row.summary === 'object' ? row.summary : {};
  const versions = new Map((Array.isArray(row?.frameworks) ? row.frameworks : []).filter((f) => f && typeof f.id === 'string').map((f) => [f.id, f.version ?? '']));
  return Object.entries(summary).map(([id, entry]) => ({ key: id, view: readinessView(frameworkInput(id, entry, versions)), name: `${FRAMEWORK_NAMES[id] ?? id} ${versions.get(id) ?? ''}`.trim() }));
}

// tierKnown false (no tierName on the customer): never an upgrade line; the API enforces tier and a refusal reads as a quiet message.
export default function ComplianceCard({ customerId, role, tier, tierKnown = true, marketplaceListingUrl = '' }) {
  const excluded = tierKnown && !complianceAllowedForTier(tier);
  const [state, setState] = useState({ phase: 'loading', rows: [] });

  useEffect(() => {
    if (!role || excluded) return undefined;
    let live = true;
    api.complianceReports(customerId).then(
      (out) => { if (live) setState({ phase: 'ready', rows: withFrameworks(out?.reports, role) }); },
      () => { if (live) setState({ phase: 'error', rows: [] }); },
    );
    return () => { live = false; };
  }, [customerId, role, excluded]);

  const rows = state.rows;
  const latest = useMemo(() => [...rows].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))[0] ?? null, [rows]);
  const latestOk = useMemo(() => [...rows].filter((r) => r.status === 'succeeded').sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))[0] ?? null, [rows]);

  if (!role) return null;
  if (excluded) {
    return (
      <p data-testid="compliance-card-upgrade" className="text-sm text-ipc-muted">
        {COMPLIANCE_FEATURE.title}.{' '}
        {marketplaceListingUrl && (
          <><a className="text-ipc-light hover:text-ipc-primary underline" href={marketplaceListingUrl} target="_blank" rel="noopener noreferrer">Change plan on AWS Marketplace</a>{' '}</>
        )}
        <a className="text-ipc-light hover:text-ipc-primary underline" href={COMPLIANCE_FEATURE.mailto}>{COMPLIANCE_FEATURE.requestLabel}</a>
      </p>
    );
  }

  const content = role === 'admin' || role === 'auditor';
  const frameworks = content && latestOk ? cardFrameworks(latestOk) : [];
  const shown = content ? (latestOk ?? latest) : latest;

  return (
    <SectionCard
      title="Compliance readiness"
      testId="compliance-card"
      bodyClassName="space-y-3"
      action={<Link className="text-ipc-light hover:text-ipc-primary underline" to={`/customers/${encodeURIComponent(customerId)}/reports`}>View Compliance</Link>}
    >
      {state.phase === 'loading' && <PageState state="loading" header={false} cards={0} label="Loading compliance status" />}
      {state.phase === 'error' && <p role="status" className="text-sm text-ipc-muted">Compliance status is not available right now.</p>}
      {state.phase === 'ready' && !shown && <p className="text-sm text-ipc-muted">No report yet</p>}
      {state.phase === 'ready' && shown && (
        <>
          {(!content || !latestOk) && <p className="text-sm text-slate-700">{lastReportLine(shown)}</p>}
          {content && latestOk && (
            <>
              <ul className="grid gap-2 sm:grid-cols-2">
                {frameworks.map((f) => (
                  <li key={f.key} data-testid="compliance-card-framework" data-tone={f.view.tone} data-level={f.view.level} className={`${READINESS_CARD} ${READINESS_ACCENT[f.view.level] ?? READINESS_ACCENT.no_report} px-3 py-2`}>
                    <p className="text-sm font-medium text-slate-700">{f.name}</p>
                    {f.view.percentText ? <p className="text-lg font-semibold text-slate-900">{f.view.percentText}</p> : null}
                    <p className="text-sm text-slate-800">{f.view.label}</p>
                    <p className="text-xs text-slate-700">{f.view.action}</p>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-ipc-muted">{READINESS_NOTE}</p>
              {mentionsCis(frameworks.map((f) => f.key)) ? <CisNotice /> : null}
              <p className="text-xs text-ipc-muted">
                {lastReportLine(shown)}
                {shown.draft && <span className="ml-2 badge-amber" title="Catalogs or mapping not yet signed off">Draft</span>}
              </p>
            </>
          )}
        </>
      )}
    </SectionCard>
  );
}
